import { useState } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, CheckCircle, CircleAlert } from "lucide-react";
import { useLocation } from "wouter";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useVoucherDetails, useDeleteStockTransaction } from "@/hooks/useStockTransactions";
import { useInvoices } from "@/hooks/useInvoices";
import { useToast } from "@/hooks/use-toast";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const StockInDetail = () => {
  const { format: formatDecimal } = useDecimalPrecision();
  const [_, navigate] = useLocation();
  const voucherNumber = window.history.state?.voucherNumber ?? null;
  const isEditMode = window.history.state?.isEditMode ?? false;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, error } = useVoucherDetails(voucherNumber);
  const { invoices, isLoading: isLoadingInvoices, deleteInvoiceAsync } = useInvoices();
  const deleteStockTransaction = useDeleteStockTransaction();

  // Redirect to edit mode if needed
  if (isEditMode && voucherNumber) {
    navigate(`/stock-in/edit`, { state: { voucherNumber } });
    return null;
  }

  if (isLoading || isLoadingInvoices) return <LoadingIndicator />;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          Error loading Stock In details: {error.message}
        </div>
      </div>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
          No Stock In transactions found for voucher {voucherNumber}
        </div>
      </div>
    );
  }

  const transactions = data.data;
  const firstTransaction = transactions[0];

  // Check if this is an invoice transaction (Self-Billed Invoice or Regular Invoice)
  // 1. Regular Invoice (Type 01) → creates INV type transactions
  // 2. Self-Billed Invoice (Type 11) → creates GI type transactions
  // 3. Regular Stock In → creates GI type transactions (from Stock Management module)
  // 4. Stock Adjustments → creates GI/GO type transactions with "Stock Adjustment" in details
  const isInvoiceTransaction =
    firstTransaction.type === "INV" ||
    (invoices?.some((inv: any) => inv.codeNumber === voucherNumber) ?? false); 

  // Check if this is a stock adjustment transaction
  const isStockAdjustment = firstTransaction.details?.includes("Stock Adjustment") ||
    firstTransaction.details?.includes("Physical Count");

  // Extract adjustment type and reason from details field
  let adjustmentType = "";
  let adjustmentReason = "";
  if (isStockAdjustment && firstTransaction.details) {
    if (firstTransaction.details.includes("Stock Adjustment (Relative)")) {
      adjustmentType = "Stock Adjustment (Relative)";
      adjustmentReason = firstTransaction.details.replace("Stock Adjustment (Relative): ", "");
    } else if (firstTransaction.details.includes("Physical Count - Stock Take")) {
      adjustmentType = "Stock Adjustment (Physical Count)";
      adjustmentReason = firstTransaction.details.replace("Physical Count - Stock Take: ", "");
    }
  }

  // Calculate totals
  const totalQuantity = transactions.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0
  );
  const totalAmount = transactions.reduce(
    (sum, item) => {
      const isInvoice =
        item.type === "INV" ||
        (invoices?.some((inv: any) => inv.codeNumber === item.voucherNumber) ?? false);
      if (isInvoice) {
        return sum + (item.totalForeignCurrencyCostIn || item.totalBaseCurrencyCostIn || 0);
      }
      return sum + (item.totalBaseCurrencyCostIn || 0);
    },
    0
  );

  const handleEdit = () => {
    if (isInvoiceTransaction) {
      // Find the invoice by voucher number
      const invoice = invoices?.find((inv: any) => inv.codeNumber === voucherNumber);

      if (invoice) {
        // Redirect to invoice edit page for self-billed invoices (Type 11)
        navigate(`/invoice-details/11`, { state: { id: invoice.id } });
      } else {
        console.error("Invoice not found with voucher number:", voucherNumber);
      }
    } else {
      // Regular stock in edit
      navigate(`/stock-in/edit`, { state: { voucherNumber } });
    }
  };

  const handleDelete = async () => {
    try {
      if (isInvoiceTransaction) {
        // Delete invoice (auto-reverse stock transactions)
        const invoice = invoices?.find((inv: any) => inv.codeNumber === voucherNumber);

        if (!invoice) {
          toast({
            title: "Error",
            description: "Invoice not found",
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
          });
          return;
        }

        await deleteInvoiceAsync(invoice.id);

        toast({
          title: "Invoice Deleted",
          description: `Invoice ${voucherNumber} has been deleted successfully`,
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
        });
      } else {
        // Delete regular stock in transaction
        await deleteStockTransaction.mutateAsync(voucherNumber);

        toast({
          title: "Stock In Deleted",
          description: `Voucher ${voucherNumber} has been deleted successfully`,
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
        });
      }

      navigate("/stock-in");
    } catch (error: any) {
      toast({
        title: "Failed to Delete",
        description: error.response?.data?.message || error.message || "An error occurred",
        variant: "destructive",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
      });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/stock-in")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Stock In Details</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleEdit}>
            Edit {isInvoiceTransaction ? "Invoice" : "Voucher"}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Voucher Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Date
                  </Label>
                  <div className="mt-1 text-base">
                    {format(new Date(firstTransaction.entryDate), "dd MMM yyyy")}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Voucher Number
                  </Label>
                  <div className="mt-1 text-base font-medium">
                    {firstTransaction.voucherNumber}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Type
                  </Label>
                  <div className="mt-1 text-base">
                    {isInvoiceTransaction
                      ? "Self-Billed Invoice"
                      : isStockAdjustment
                        ? adjustmentType
                        : "Stock In"}
                  </div>
                </div>

                {/* Only show reference and remarks/reason for non-invoice transactions */}
                {!isInvoiceTransaction && (
                  <>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">
                        Reference
                      </Label>
                      <div className="mt-1 text-base">
                        {firstTransaction.reference || "-"}
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-gray-600">
                        {isStockAdjustment ? "Reason" : "Remarks"}
                      </Label>
                      <div className="mt-1 text-base">
                        {isStockAdjustment ? adjustmentReason : (firstTransaction.details || "-")}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Line Items Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Line Items</h3>

              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead>Item Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">
                      {isInvoiceTransaction
                        ? "Unit Price (RM)"
                        : "Cost Price (RM)"}
                    </TableHead>
                    <TableHead className="text-right">Amount (RM)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => {
                    const isInvoice =
                      transaction.type === "INV" ||
                      (invoices?.some((inv: any) => inv.codeNumber === transaction.voucherNumber) ?? false);
                    // For stock in: Calculate actual cost price from totalCost / quantity
                    const actualCostPrice = transaction.quantity && transaction.quantity > 0
                      ? (transaction.totalBaseCurrencyCostIn || 0) / transaction.quantity
                      : 0;
                    const unitPrice = isInvoice
                      ? (transaction.foreignCurrencyCostIn || transaction.baseCurrencyCostIn || 0)
                      : actualCostPrice;
                    const totalAmount = isInvoice
                      ? (transaction.totalForeignCurrencyCostIn || transaction.totalBaseCurrencyCostIn || 0)
                      : (transaction.totalBaseCurrencyCostIn || 0);

                    return (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-medium">
                          {transaction.itemCode}
                        </TableCell>
                        <TableCell>
                          {transaction.itemDescription || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-green-600 font-medium">
                            +{formatDecimal(Number(transaction.quantity || 0))}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDecimal(Number(unitPrice))}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatDecimal(Number(totalAmount))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-96 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Total Quantity:</span>
                    <span className="text-green-600 font-medium">
                      +{formatDecimal(totalQuantity)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total Amount:</span>
                    <span>RM {formatDecimal(totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {isInvoiceTransaction ? "Invoice" : "Stock In Voucher"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will delete {isInvoiceTransaction ? "invoice" : "voucher"} {voucherNumber} and reverse all stock movements.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
